import React, { useEffect, useState } from "react";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import api from "@/lib/api";
import { Plus, ArrowDownLeft, ArrowUpRight } from "lucide-react";

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const [txs, setTxs] = useState([]);
  const [amount, setAmount] = useState(20);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/wallet/transactions").then(r => setTxs(r.data));
  useEffect(() => { load(); }, []);

  const topup = async () => {
    setLoading(true);
    await api.post("/wallet/topup", { amount: Number(amount) });
    await refreshUser();
    await load();
    setLoading(false);
  };

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold">{t("wallet")}</h1>

        <div className="mt-5 rounded-3xl p-6 text-white so-gradient-hero so-grain relative overflow-hidden">
          <div className="text-xs uppercase tracking-widest text-white/60">Available balance</div>
          <div className="mt-1 text-4xl font-extrabold" data-testid="wallet-balance">{formatPrice(user?.wallet_balance)}</div>
          <div className="mt-1 text-sm text-white/70">{user?.loyalty_points} loyalty pts · {user?.loyalty_tier} tier</div>
        </div>

        <div className="mt-5 so-card p-5">
          <div className="font-bold mb-3">{t("topUp")}</div>
          <div className="flex gap-2">
            {[10, 20, 50, 100].map(v => (
              <button key={v} onClick={() => setAmount(v)} data-testid={`amount-${v}`} className={`flex-1 py-2 rounded-xl border-2 font-bold ${amount === v ? "border-[var(--so-orange)] text-[var(--so-orange)]" : "border-[var(--so-border)]"}`}>{formatPrice(v)}</button>
            ))}
          </div>
          <button onClick={topup} disabled={loading} className="so-btn-primary w-full justify-center mt-3" data-testid="topup-btn">
            <Plus className="w-4 h-4" /> {t("addBalance")} {formatPrice(amount)}
          </button>
        </div>

        <div className="mt-5">
          <div className="font-bold mb-2">Recent transactions</div>
          {txs.length === 0 ? (
            <div className="text-sm text-[var(--so-text-soft)] py-4">No transactions yet.</div>
          ) : (
            <div className="space-y-2">
              {txs.map((tx) => (
                <div key={tx.id} className="so-card p-3 flex items-center gap-3" data-testid={`tx-${tx.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tx.type === "credit" ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"}`}>
                    {tx.type === "credit" ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{tx.description}</div>
                    <div className="text-xs text-[var(--so-text-soft)]">{new Date(tx.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`font-bold ${tx.type === "credit" ? "text-emerald-600" : "text-red-600"}`}>
                    {tx.type === "credit" ? "+" : "-"}{formatPrice(tx.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}
